import * as React from 'react';
import { styled } from '@mui/material/styles';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuContent from './MenuContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { useSelector } from 'react-redux';
import { usersApi } from '~/features/user/model/api/users-api';
import ColorModeIconDropdown from '~/shared/theme/ColorModeIconDropdown';
import { deleteCookie, getCookie } from '~/shared/utils/cookie';
import { LogoMangoIcon } from '~/shared/icons/LogoMangoIcon';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ collapsed }) => ({
  width: collapsed ? collapsedDrawerWidth : drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  mt: 10,
  [`& .${drawerClasses.paper}`]: {
    width: collapsed ? collapsedDrawerWidth : drawerWidth,
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },
}));

export default function SideMenu() {

  const { data } = useSelector(usersApi.endpoints.getUserInfo.select(undefined)) as any;
  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const [tokenCopied, setTokenCopied] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    return localStorage.getItem('aside_collapsed') === 'true';
  });
  const shouldShowToken = React.useMemo(() => {
    return import.meta.env.DEV || String(import.meta.env.VITE_DEV_AUTH).toLowerCase() === 'true';
  }, []);
  const debugToken = shouldShowToken ? (getCookie('auth-token') || '') : '';

  const handleLogout = React.useCallback(() => {
    deleteCookie('auth-token');
    location.href = '/logout';
  }, []);

  const handleCopyToken = React.useCallback(async () => {
    if (!debugToken) return;
    try {
      await navigator.clipboard.writeText(debugToken);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = debugToken;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 1200);
  }, [debugToken]);

  return (
    <Drawer
      variant="permanent"
      collapsed={collapsed}
      sx={{
        display: { xs: 'none', md: 'block' },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: 'background.paper',
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          mt: 'calc(var(--template-frame-height, 0px) + 4px)',
          p: collapsed ? 1 : 2,
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          flexDirection: collapsed ? 'column' : 'row',
          gap: collapsed ? 1 : 0,
        }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          
          {!collapsed && (
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Mango CPA
            </Typography>
          )}
        </Stack>
        <Stack
          direction={collapsed ? 'column' : 'row'}
          alignItems="center"
          spacing={0.5}
        >
          <Tooltip title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}>
            <IconButton
              size="small"
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
              onClick={() => {
                setCollapsed(prev => {
                  const next = !prev;
                  localStorage.setItem('aside_collapsed', String(next));
                  return next;
                });
              }}
            >
              {collapsed ? <ChevronRightRoundedIcon /> : <ChevronLeftRoundedIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Divider />
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <MenuContent collapsed={collapsed} />
      </Box>
      <Stack
  direction="row"
  sx={{
    p: 2,
    gap: 1.5,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    borderTop: '1px solid',
    borderColor: 'divider',
  }}
>
  {!collapsed ? (
    <>
      <Stack
  direction="column"
  sx={{
    minWidth: 0,
    flex: 1,
    height: '100%',
    justifyContent: 'space-between',
  }}
>

  {/* Текст */}
  <Box>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, lineHeight: '16px' }}
    >
      {data?.name}
    </Typography>

    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {data?.email}
    </Typography>

    {debugToken ? (
      <Tooltip title={tokenCopied ? 'Скопировано' : 'Нажмите, чтобы скопировать токен'}>
        <Typography
          variant="caption"
          onClick={handleCopyToken}
          sx={{
            display: 'block',
            mt: 0.25,
            color: 'text.secondary',
            fontFamily: 'monospace',
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 190,
          }}
        >
          token: {debugToken}
        </Typography>
      </Tooltip>
    ) : null}
  </Box>

  {/* Кнопки */}
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    sx={{ mt: 1 }}
  >
    <ColorModeIconDropdown
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: 35,
        height: 35,
      }}
    />

    <Tooltip title="Выход">
      <IconButton
        size="small"
        color="primary"
        onClick={() => setLogoutOpen(true)}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          width: 35,
          height: 35,
        }}
      >
        <LogoutRoundedIcon />
      </IconButton>
    </Tooltip>
  </Stack>

</Stack>
    </>
  ) : (
    <Stack
      direction="column"
      spacing={1}
      alignItems="center"
      sx={{ width: '100%' }}
    >
      <ColorModeIconDropdown
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      />

      <Tooltip title="Выход">
        <IconButton
        size="small"
          color="primary"
          onClick={() => setLogoutOpen(true)}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <LogoutRoundedIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  )}
</Stack>
      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)}>
        <DialogTitle>Подтвердите выход</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите выйти из приложения?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)}>Отмена</Button>
          <Button onClick={handleLogout} color="primary">Выйти</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
